/**
 * Portal Materials Management API Client
 *
 * Handles CRUD operations for materials with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

// Year options (Kindy, Pre-primary, Year 1-12)
export const YEARS = [
  { value: -1, label: 'Kindy' },
  { value: 0, label: 'Pre-primary' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `Year ${i + 1}`
  }))
];

// Term options
export const TERMS = [
  { value: 0, label: 'Special Term' },
  { value: 1, label: 'Term 1' },
  { value: 2, label: 'Term 2' },
  { value: 3, label: 'Term 3' },
  { value: 4, label: 'Term 4' }
];

// Week options (1-12)
export const WEEKS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: `Week ${i + 1}`
}));

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to material-admin endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/material-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * Make an authenticated API request to material-view-print endpoint.
 */
const viewPrintApiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/material-view-print`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * List all materials with optional filters.
 *
 * @param {Object} filters - Optional filters
 * @param {string} filters.subject - Filter by subject
 * @param {number} filters.year - Filter by year
 * @param {number} filters.term - Filter by term
 * @param {number} filters.week - Filter by week
 * @param {boolean} filters.isAnswer - Filter by isAnswer
 */
export const listMaterials = async (filters = {}) => {
  const result = await apiRequest('list', { filters });
  return result.materials || [];
};

/**
 * Create a new material and get presigned upload URL.
 *
 * @param {Object} materialData - Material data
 * @param {string} materialData.name - Display name
 * @param {string[]} materialData.subjectIds - Array of subject IDs (supports multi-subject)
 * @param {number} materialData.year - Year level
 * @param {number} materialData.term - Term number
 * @param {number} materialData.week - Week number
 * @param {boolean} materialData.isAnswer - Is this an answer sheet
 * @param {string} materialData.filename - Original filename
 * @param {string} materialData.contentType - MIME type
 */
export const createMaterial = async (materialData) => {
  const result = await apiRequest('create', materialData);
  return {
    material: result.material,
    uploadUrl: result.uploadUrl,
    contentType: result.contentType
  };
};

/**
 * Upload file to S3 using presigned URL.
 *
 * @param {string} uploadUrl - Presigned S3 URL
 * @param {File} file - File to upload
 * @param {string} contentType - Content-Type that matches the presigned URL signature
 */
export const uploadFileToS3 = async (uploadUrl, file, contentType) => {
  // Use the exact Content-Type that was used to generate the presigned URL
  // This ensures the signature matches and the upload succeeds
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType
    },
    body: file
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('S3 upload failed:', response.status, errorText);
    throw new Error(`Failed to upload file to S3: ${response.status}`);
  }

  return true;
};

/**
 * Update a material.
 * Only Directors and System Admins can update.
 *
 * @param {string} materialId - Material ID
 * @param {Object} updates - Fields to update (supports subjectIds array for multi-subject)
 */
export const updateMaterial = async (materialId, updates) => {
  const result = await apiRequest('update', { materialId, ...updates });
  return result.material;
};

/**
 * Delete a material.
 * Only Directors and System Admins can delete.
 *
 * @param {string} materialId - Material ID
 */
export const deleteMaterial = async (materialId) => {
  const result = await apiRequest('delete', { materialId });
  return result;
};

/**
 * Get unique subjects list from existing materials.
 */
export const getSubjects = async () => {
  const result = await apiRequest('getSubjects');
  return result.subjects || [];
};

/**
 * Get presigned URL for re-uploading a file to an existing material.
 * Only Directors and System Admins can use this.
 *
 * @param {string} materialId - Material ID
 * @param {string} filename - New filename
 * @param {string} contentType - MIME type (defaults to 'application/pdf')
 * @returns {Object} { uploadUrl, s3Key, contentType }
 */
export const getUploadUrlForUpdate = async (materialId, filename, contentType = 'application/pdf') => {
  const result = await apiRequest('getUploadUrl', { materialId, filename, contentType });
  return {
    uploadUrl: result.uploadUrl,
    s3Key: result.s3Key,
    contentType: result.contentType
  };
};

/**
 * Parse filename locally (client-side) for Year/Term/Week info.
 *
 * @param {string} filename - Filename to parse
 * @returns {Object} Parsed values { year, term, week } or empty object
 */
export const parseFilenameLocal = (filename) => {
  const patterns = [
    /Y(\d+)T(\d+)W(\d+)/i,           // Y3T2W1
    /Year(\d+)Term(\d+)Week(\d+)/i,   // Year3Term2Week1
    /(\d+)-(\d+)-(\d+)/,              // 3-2-1
  ];

  for (const pattern of patterns) {
    const match = filename.match(pattern);
    if (match) {
      return {
        year: parseInt(match[1]),
        term: parseInt(match[2]),
        week: parseInt(match[3])
      };
    }
  }
  return {};
};

/**
 * Check if the current user can View/Print a material.
 *
 * @param {string} materialId - Material ID to check
 * @returns {Object} Access information:
 *   - canView: boolean - Whether user can view
 *   - canPrint: boolean - Whether user can print
 *   - viewCount: number - Number of times viewed
 *   - viewLimit: number|null - View limit (null = unlimited)
 *   - printCount: number - Number of times printed
 *   - printLimit: number|null - Print limit (null = unlimited, 0 = not allowed)
 *   - role: string - User's highest portal role
 */
export const checkMaterialAccess = async (materialId) => {
  const result = await viewPrintApiRequest('checkAccess', { materialId });
  return {
    canView: result.canView,
    canPrint: result.canPrint,
    viewCount: result.viewCount,
    viewLimit: result.viewLimit,
    printCount: result.printCount,
    printLimit: result.printLimit,
    role: result.role
  };
};

/**
 * Get presigned URL for viewing a material (records the view operation).
 *
 * @param {string} materialId - Material ID to view
 * @returns {Object} View information:
 *   - url: string - Presigned URL to access the PDF
 *   - expiresIn: number - Seconds until URL expires
 *   - remaining: number|undefined - Remaining view count (only for limited roles)
 *   - materialName: string - Name of the material
 */
export const getMaterialViewUrl = async (materialId) => {
  const result = await viewPrintApiRequest('recordAccess', {
    materialId,
    operationType: 'VIEW'
  });
  return {
    url: result.url,
    expiresIn: result.expiresIn,
    remaining: result.remaining,
    materialName: result.materialName
  };
};

/**
 * Get presigned URL for printing a material (records the print operation).
 *
 * @param {string} materialId - Material ID to print
 * @returns {Object} Print information:
 *   - url: string - Presigned URL to access the PDF
 *   - expiresIn: number - Seconds until URL expires
 *   - remaining: number|undefined - Remaining print count (only for limited roles)
 *   - materialName: string - Name of the material
 */
export const getMaterialPrintUrl = async (materialId) => {
  const result = await viewPrintApiRequest('recordAccess', {
    materialId,
    operationType: 'PRINT'
  });
  return {
    url: result.url,
    expiresIn: result.expiresIn,
    remaining: result.remaining,
    materialName: result.materialName
  };
};

/**
 * Make an authenticated API request to material-control endpoint.
 */
const controlApiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/material-control`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * Get all extra access grants for a material.
 *
 * @param {string} materialId - Material ID
 * @returns {Array} List of control records with user info
 */
export const getMaterialControls = async (materialId) => {
  const result = await controlApiRequest('listControls', { materialId });
  return result.controls || [];
};

/**
 * Set or update extra View/Print counts for a user on a material.
 *
 * @param {string} materialId - Material ID
 * @param {string} userId - User ID (from UserPortal)
 * @param {number} extraViewCount - Extra view count to grant
 * @param {number} extraPrintCount - Extra print count to grant
 * @returns {Object} The created/updated control record
 */
export const setMaterialControl = async (materialId, userId, extraViewCount, extraPrintCount) => {
  const result = await controlApiRequest('setControl', {
    materialId,
    userId,
    extraViewCount,
    extraPrintCount
  });
  return result.control;
};

/**
 * Delete an extra access grant.
 *
 * @param {string} controlId - Control record ID
 */
export const deleteMaterialControl = async (controlId) => {
  const result = await controlApiRequest('deleteControl', { controlId });
  return result;
};

/**
 * Make an authenticated API request to material-answer endpoint.
 */
const answerApiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/material-answer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * Get all answers linked to a material.
 *
 * @param {string} materialId - Material ID
 * @returns {Array} List of linked answers with details
 */
export const getLinkedAnswers = async (materialId) => {
  const result = await answerApiRequest('listLinkedAnswers', { materialId });
  return result.links || [];
};

/**
 * Get every material-answer link in one call (id / materialId / answerId only).
 * Used by the materials list to show which materials already have an answer.
 *
 * @returns {Array} List of link records
 */
export const listAllAnswerLinks = async () => {
  const result = await answerApiRequest('listAllLinks', {});
  return result.links || [];
};

/**
 * Link an answer to a material.
 *
 * @param {string} materialId - Material ID (the question)
 * @param {string} answerId - Answer material ID (must have isAnswer=true)
 * @returns {Object} The created link record
 */
export const linkAnswer = async (materialId, answerId) => {
  const result = await answerApiRequest('linkAnswer', { materialId, answerId });
  return result.link;
};

/**
 * Unlink an answer from a material.
 *
 * @param {string} linkId - Link record ID
 */
export const unlinkAnswer = async (linkId) => {
  const result = await answerApiRequest('unlinkAnswer', { linkId });
  return result;
};

/**
 * Get all available answer materials that the user can access.
 * Teachers only see their own answer materials.
 * Admins see all answer materials.
 *
 * @returns {Array} List of answer materials
 */
export const listAvailableAnswers = async () => {
  const result = await answerApiRequest('listAvailableAnswers', {});
  return result.answers || [];
};

const materialPortalApi = {
  listMaterials,
  createMaterial,
  uploadFileToS3,
  updateMaterial,
  deleteMaterial,
  getSubjects,
  getUploadUrlForUpdate,
  parseFilenameLocal,
  checkMaterialAccess,
  getMaterialViewUrl,
  getMaterialPrintUrl,
  getMaterialControls,
  setMaterialControl,
  deleteMaterialControl,
  getLinkedAnswers,
  listAllAnswerLinks,
  linkAnswer,
  unlinkAnswer,
  listAvailableAnswers,
  YEARS,
  TERMS,
  WEEKS
};

export default materialPortalApi;
